import React, { useEffect } from 'react'
import Logger from '#src/services/logger'
import classNames from 'classnames'
import Logo from '#src/components/Logo'
import ContentfulQuery, { ContentfulImage } from '#src/containers/Contentful'
import QuestionHeader from './components/QuestionHeader'
import PageHeader from '#src/components/Path/components/PageHeader'
import InputHeader from './components/InputHeader'
import ErrorHeader from '#src/components/Path/components/ErrorHeader'
import DepartmentReviews from '#src/components/DepartmentReviews'
import Interstitial, { InterstitialMap } from '#src/components/Path/Interstitials'
import MetaData, { MetaDataComponentMap } from '#src/components/Path/MetaData'
import { NotFound } from '#src/components/Errors'
import PageContent from '#src/components/PageContent'
import BrandAwareLogo from '#src/components/Logo'
import Copyright from '#src/components/Copyright'
import {
  InterstitialPathFlowPageMetadataType,
  NumericPathFlowQuestionFragment,
  ListPathFlowQuestionFragment,
  InputTextPathFlowQuestionFragment
} from '#src/generated/graphql'
import Question, { QuestionComponentMap, PathQuestionProps } from '../../Question'
import { Button, FullBlockLoader, CircleLoader } from '@deal/components'
import { usePathContext } from '#src/components/Path/PathContext'
import { LayoutProps } from '../'
import Check from './check.svg'
import styles from './styles.css'

interface BubbaPayloutContentfulResponse {
  loading: boolean
  content: {
    backgroundImage: ContentfulImage
    pageHeadline?: string
    whatToExpectHeadline?: string
    whatToExpectSubHeader?: string
    bullets: {
      value: string
    }[]
    whiteBoxHeader?: string
    whiteBoxBody?: {
      body: string
    }[]
  }
}

// Questions
import InputTextPathFlowQuestionComponent from '#src/components/Path/components/InputTextPathFlowQuestion'
import IdPathFlowQuestionComponent from '#src/components/Path/components/IdPathFlowQuestion'
import ListPathFlowQuestionComponent from '#src/components/Path/components/ListPathFlowQuestion'
import NumericPathFlowQuestionComponent from '#src/components/Path/components/NumericPathFlowQuestion'
import OAuthEmailPathFlowQuestionComponent from '#src/components/Path/components/OAuthEmailPathFlowQuestion'
import DateRangePathFlowQuestionComponent from '#src/components/Path/components/DateRangePathFlowQuestion'
import DatePathFlowQuestionComponent from '#src/components/Path/components/DatePathFlowQuestion'

// Interstitials
import GenericInterstitial from './components/GenericInterstitial'
import GenericWithButtonInterstitial from './components/GenericWithButtonInterstitial'
import RequestPushNotificationInterstitial from '#src/components/Path/components/RequestPushNotificationsInterstitial'
import WhatHappensNextInterstitial from '#src/components/Path/components/WhatHappensNextInterstitial'

// Metadata
import TermsPrivacyMedicalMetadata from '#src/components/Path/components/TermsPrivacyMedicalMetadata'
import TermsAndConditionsMetadata from '#src/components/Path/components/TermsAndConditionsMetadata'
import ContactPrivacyMetadata from '#src/components/Path/components/ContactPrivacyMetadata'
import ContactTCPAComplianceMetadata from '#src/components/Path/components/ContactTCPAComplianceMetadata'
import RatingContactPageMetadataHeader from '#src/components/Path/components/RatingContactPageMetadata/Header'
import RatingContactPageMetadataFooter from '#src/components/Path/components/RatingContactPageMetadata/Footer'
import BackgroundImage from '#src/components/BackgroundImage'

const ListPathFlowQuestionComponentWrapper: React.SFC<PathQuestionProps<
  ListPathFlowQuestionFragment
>> = ({ ...rest }) => {
  return (
    <ListPathFlowQuestionComponent {...rest} className={styles.radioLabel} display="standard" />
  )
}

const InputTextPathFlowQuestionWrapper: React.SFC<PathQuestionProps<
  InputTextPathFlowQuestionFragment
>> = ({ ...rest }) => {
  return <InputTextPathFlowQuestionComponent {...rest} display="standard" />
}

const NumericPathFlowQuestionWrapper: React.SFC<PathQuestionProps<
  NumericPathFlowQuestionFragment
>> = ({ ...rest }) => {
  return <NumericPathFlowQuestionComponent {...rest} display="standard" />
}

const BubbaLayout: React.SFC<LayoutProps> = ({
  areAllQuestionsOptional,
  areAllQuestionsAnswered,
  questions,
  className,
  onUpdateScrollRef
}) => {
  const questionMap: QuestionComponentMap = {
    InputTextPathFlowQuestion: InputTextPathFlowQuestionWrapper,
    IdPathFlowQuestion: IdPathFlowQuestionComponent,
    ListPathFlowQuestion: ListPathFlowQuestionComponentWrapper,
    NumericPathFlowQuestion: NumericPathFlowQuestionWrapper,
    OAuthEmailPathFlowQuestion: OAuthEmailPathFlowQuestionComponent,
    DateRangePathFlowQuestion: DateRangePathFlowQuestionComponent,
    DatePathFlowQuestion: DatePathFlowQuestionComponent
  }

  const interstitialMap: InterstitialMap = {
    [InterstitialPathFlowPageMetadataType.GENERIC_WITH_TITLE]: GenericInterstitial,
    [InterstitialPathFlowPageMetadataType.GENERIC_WITH_BUTTON]: GenericWithButtonInterstitial,
    [InterstitialPathFlowPageMetadataType.REQUEST_PUSH_NOTIFICATION_PERMISSION]: RequestPushNotificationInterstitial,
    [InterstitialPathFlowPageMetadataType.WHAT_HAPPENS_NEXT]: WhatHappensNextInterstitial
  }

  const metaDataMap: MetaDataComponentMap = {
    TermsPrivacyMedicalPathFlowPageMetadata: TermsPrivacyMedicalMetadata,
    TermsAndConditionsPathFlowPageMetadata: TermsAndConditionsMetadata,
    ContactPrivacyMetadata: ContactPrivacyMetadata,
    ContactTCPAComplianceMetadata: ContactTCPAComplianceMetadata
  }

  useEffect(() => {
    onUpdateScrollRef(window)
  }, [])

  const pathContext = usePathContext()

  const topLevelCategory = pathContext.pathFlow.category
    ? pathContext.pathFlow.category.department
      ? pathContext.pathFlow.category.department.topLevelCategory
      : pathContext.pathFlow.category
    : undefined
  const departmentId =
    pathContext.pathFlow.category && pathContext.pathFlow.category.department
      ? pathContext.pathFlow.category.department.id
      : undefined

  const submitButtonText = pathContext.pathFlowPage.submitButtonText
    ? pathContext.pathFlowPage.submitButtonText.text
    : 'Submit'
  const skipButtonText = pathContext.pathFlowPage.skipButtonText
    ? pathContext.pathFlowPage.skipButtonText.text
    : 'Skip'

  if (topLevelCategory && topLevelCategory.slug) {
    return (
      <ContentfulQuery template="bubbaPathLayout" variation={topLevelCategory.slug}>
        {({ content, loading }: BubbaPayloutContentfulResponse) => {
          if (content) {
            return (
              <div
                className={classNames(
                  'render-path-default',
                  styles.pageContainer,
                  styles.fixedBody
                )}
              >
                <BackgroundImage
                  src={content.backgroundImage.imageUrl}
                  size="100vw"
                  containerClassName={classNames(styles.pageContainer, className)}
                >
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${pathContext.pathFlowPage.completionPercentage}%` }}
                    />
                  </div>
                  <div className={styles.container}>
                    <div className={styles.logo}>
                      <Logo className={styles.logoMark} color="light" mark="word" />
                    </div>

                    <ErrorHeader />

                    {content.pageHeadline && (
                      <div className={styles.pageHeadline}>{content.pageHeadline}</div>
                    )}

                    <PageHeader />

                    {questions.length === 0 && (
                      <div className={styles.interstitial}>
                        <Interstitial
                          key={pathContext.pathFlowPage.internalName}
                          interstitialMap={interstitialMap}
                          submitButtonText={submitButtonText}
                        />
                      </div>
                    )}

                    <RatingContactPageMetadataHeader
                      categorySlug={topLevelCategory && topLevelCategory.slug}
                    />

                    {questions.length > 0 && (
                      <form
                        onSubmit={
                          !pathContext.isSubmitting ? pathContext.onPageSubmitted : undefined
                        }
                        className={styles.form}
                      >
                        <div className={styles.questionData}>
                          {questions.map((question, index) => (
                            <React.Fragment key={index}>
                              <QuestionHeader
                                questionText={question.questionText}
                                questionSubText={question.questionSubText}
                              />
                              <InputHeader
                                inputLabel={question.inputLabel}
                                inputIconImage={question.inputIconImage}
                              />
                              <Question
                                key={question.internalName}
                                questionIndex={index}
                                question={question}
                                questionComponentMap={questionMap}
                              />
                            </React.Fragment>
                          ))}

                          <div>
                            <RatingContactPageMetadataFooter />
                            <MetaData metaDataComponents={metaDataMap} />
                          </div>
                        </div>
                        <div className={styles.actions}>
                          {!pathContext.isBackButtonHidden && (
                            <div
                              className={classNames({
                                [styles.backNoSubmit]: pathContext.isSubmitButtonHidden,
                                [styles.backWithSubmit]: !pathContext.isSubmitButtonHidden
                              })}
                            >
                              <Button
                                type="button"
                                size="large"
                                variant="contrast-light"
                                tabIndex={2}
                                onClick={pathContext.onPageBack}
                                disabled={pathContext.isSubmitting}
                              >
                                {pathContext.isSubmitting && pathContext.isSubmitButtonHidden
                                  ? 'Submitting...'
                                  : 'Back'}
                              </Button>
                            </div>
                          )}
                          {!pathContext.isSubmitButtonHidden && (
                            <div className={styles.submit}>
                              <Button
                                type="button"
                                size="large"
                                tabIndex={1}
                                disabled={!pathContext.isFormValid || pathContext.isSubmitting}
                                onClick={() => pathContext.onPageSubmitted()}
                              >
                                {pathContext.isSubmitting
                                  ? 'Submitting...'
                                  : areAllQuestionsOptional
                                  ? areAllQuestionsAnswered
                                    ? submitButtonText
                                    : skipButtonText
                                  : submitButtonText}
                              </Button>
                            </div>
                          )}
                        </div>
                        {content.whiteBoxBody && content.whiteBoxHeader && (
                          <div className={styles.explainerContainer}>
                            <div className={styles.explainer}>
                              {content.whiteBoxHeader && (
                                <div className={styles.explainerHeader}>
                                  {content.whiteBoxHeader}
                                </div>
                              )}
                              {content.whiteBoxBody.map((entry, idx) => (
                                <p key={idx}>{entry.body}</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </form>
                    )}
                  </div>
                </BackgroundImage>
                <div className={styles.whatToExpect}>
                  <div className={styles.whatToExpectHeader}>{content.whatToExpectHeadline}</div>
                  <div className={styles.whatToExpectSubHeader}>
                    {content.whatToExpectSubHeader}
                  </div>
                  <div className={styles.whatToExpectBullets}>
                    {content.bullets.map((bullet, index) => (
                      <div key={index} className={styles.bullet}>
                        <div className={styles.bulletIcon}>
                          <Check />
                        </div>
                        <span className={styles.bulletLabel}>{bullet.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {departmentId && <DepartmentReviews departmentId={departmentId} />}
                <footer className={styles.footer}>
                  <PageContent>
                    <div className={styles.footerSection}>
                      <BrandAwareLogo className={styles.footerLogo} color="light" />
                      <div className={styles.footerCopy}>
                        <Copyright />
                      </div>
                    </div>
                  </PageContent>
                </footer>
              </div>
            )
          } else if (loading) {
            return <FullBlockLoader loader={CircleLoader} />
          } else {
            Logger.captureException('No contentful entry for Bubba template category', {
              pathCategorySlug: topLevelCategory.slug
            })

            return <NotFound />
          }
        }}
      </ContentfulQuery>
    )
  } else {
    Logger.captureException(`No path category for Bubba template found`)

    return <NotFound />
  }
}

export default React.memo(BubbaLayout)
